/**
 * Reading a Settings → Engine body per plan engine (engines T3, spec
 * 2026-10-06 §5.5).
 *
 * The body carries one PlanView per plan engine enabled for the person
 * (`plans`) and the engines "Bash works on" offers (`pickable`). A server
 * before T3 sends only today's single fields, which describe Claude Code: here
 * they read as its plan, so every reader asks one question whichever server
 * answered.
 *
 * Pure, with no request behind it: the stores and the task view read it too.
 */

import {
  ENGINE_PLAN_NAME,
  ENGINE_SIGN_IN_METHODS,
  SUBSCRIPTION_ENGINES,
  isSubscriptionEngine,
  type EngineAccountView,
  type PlanEngine,
} from "@bash/shared";
import type { Engine, EngineBody, PlanView } from "@/lib/api/engine";

/** A body before T3's plans, read once: the same body always reads as the
 *  same plan object, so a reader that compares plans by identity is steady. */
const legacyPlans = new WeakMap<
  EngineBody,
  Partial<Record<PlanEngine, PlanView>>
>();

/** Each plan engine's state in the body. */
export function plansOf(
  body: EngineBody
): Partial<Record<PlanEngine, PlanView>> {
  if (body.plans) return body.plans;
  let plans = legacyPlans.get(body);
  if (!plans) {
    plans = {
      claude_code: {
        status: body.status ?? null,
        statusAt: body.statusAt ?? null,
        account: body.account ?? null,
        plan: body.plan ?? null,
        accounts: body.accounts,
        accountOrder: body.accountOrder,
        signInMethods: ENGINE_SIGN_IN_METHODS.claude_code,
      },
    };
    legacyPlans.set(body, plans);
  }
  return plans;
}

/** One plan engine's state; null when it is not enabled for the person. */
export const planOf = (body: EngineBody, engine: PlanEngine): PlanView | null =>
  plansOf(body)[engine] ?? null;

/** The plan engines the body describes, in the product's order. */
export function planEngines(body: EngineBody): PlanEngine[] {
  const plans = plansOf(body);
  return SUBSCRIPTION_ENGINES.filter((e) => plans[e] !== undefined);
}

/** What "Bash works on" offers: Vestra, then every plan engine enabled. An
 *  engine this build does not know (a server ahead of it) is never offered. */
export const pickableOf = (body: EngineBody): Engine[] =>
  (body.pickable ?? ["native", ...planEngines(body)]).filter(
    (v): v is Engine => v === "native" || isSubscriptionEngine(v)
  );

/** The body with one plan engine's state changed. The single fields are
 *  never written: every reader goes through plansOf. */
export function withPlan(
  body: EngineBody,
  engine: PlanEngine,
  change: (plan: PlanView) => PlanView
): EngineBody {
  const plan = planOf(body, engine);
  if (!plan) return body;
  return { ...body, plans: { ...plansOf(body), [engine]: change(plan) } };
}

/**
 * The name of the plan a task's engine runs on ("ChatGPT" for a Codex task),
 * for a line that names it; "your plan" for an engine id this build does not
 * know as a plan engine, never a guess.
 */
export const planNameOf = (engine: string | null | undefined): string =>
  isSubscriptionEngine(engine) ? ENGINE_PLAN_NAME[engine] : "your plan";

/** Each plan engine's accounts, where it has a list (several accounts). */
export type EngineAccounts = Partial<Record<PlanEngine, EngineAccountView[]>>;

/** The accounts lists in the body; null when no engine has one
 *  (ENGINE_MULTI_ACCOUNT_V1 off). */
export function accountsOf(body: EngineBody): EngineAccounts | null {
  const lists: EngineAccounts = {};
  for (const engine of planEngines(body)) {
    const accounts = planOf(body, engine)?.accounts;
    if (accounts) lists[engine] = accounts;
  }
  return Object.keys(lists).length > 0 ? lists : null;
}
